/**
 * Edit-mode store slice — composed from domain-specific sub-modules.
 *
 * Every method on the slice lives in exactly one of:
 *   - `selectionOps.ts`  — lifecycle, selection, hide/unhide, move
 *   - `clickOps.ts`      — the viewport click dispatcher
 *   - `groupOps.ts`      — vertex groups, material slots, group-based select
 *   - `curveOps.ts`      — curve-specific edits
 *   - `meshOps.ts`       — topology-mutating ops (delete/dissolve/subdivide/
 *                          bridge/extrude/inset/bevel/edge-loop-insert/merge/
 *                          rip/flip/recalculate/separate/cleanUp)
 *   - `edgeAttrOps.ts`   — per-edge attribute flags (seam / sharp)
 *
 * Shared infrastructure (applyResult, runEditOp, generator-bake cascade,
 * raycast bridge, flood fill, rip projection) lives in `core.ts`.
 * Pure selection-state helpers live in `../../modeling/editSelection.ts`.
 */

import { clearEditSelection } from "../../modeling/editSelection.ts";
import {
  concatTopology,
  extractSubTopology,
  triangulateTopologyForRendering,
} from "../../modeling/topology.ts";
import type { EditModeState } from "../../modeling/types.ts";
import { patchWithUVs } from "../../modeling/uvUnwrap.ts";
import { replaceSceneObject } from "../sceneObjectMap.ts";
import { createClickOps } from "./clickOps.ts";
import { computeNormalsForObject, type Get_, type Set_ } from "./core.ts";
import { createCurveOps } from "./curveOps.ts";
import { createEdgeAttrOps } from "./edgeAttrOps.ts";
import { createFaceAttrOps } from "./faceAttrOps.ts";
import { createGroupOps } from "./groupOps.ts";
import { createMeshOps } from "./meshOps.ts";
import { createSelectionOps } from "./selectionOps.ts";

// ---------------------------------------------------------------------------
// Factory — assembles the full edit slice from per-domain sub-factories
// ---------------------------------------------------------------------------

/** Edit-mode store methods — call `createEditOps(set, get)` inside the store factory. */
export function createEditOps(set: Set_, get: Get_) {
  return {
    ...createSelectionOps(set, get),
    ...createClickOps(set, get),
    ...createGroupOps(set, get),
    ...createFaceAttrOps(set, get),
    ...createCurveOps(set, get),
    ...createMeshOps(set, get),
    ...createEdgeAttrOps(set, get),
  };
}

// ---------------------------------------------------------------------------
// duplicateSelectionInEditMode — edit-mode duplicate worker
//
// Clones the currently selected vertices (and their sub-topology) in place,
// appending them to the active object and re-selecting the new geometry. The
// caller is expected to immediately enter translate-command mode so the user
// can drag the duplicate off the source, matching Blender's Shift+D UX.
//
// In-place means zero offset: the cloned vertices land at the exact world
// positions of their sources. Callers that want the duplicate to appear
// somewhere else should rely on the subsequent translate command.
//
// The caller guarantees `em` is non-null and `em.selectedVertices.size > 0`.
// ---------------------------------------------------------------------------

export function duplicateSelectionInEditMode(
  get: Get_,
  set: Set_,
  em: EditModeState,
): void {
  const obj = get().sceneObjects.get(em.objectId)!;
  const selected = [...em.selectedVertices].sort((a, b) => a - b);
  const existingVertCount = obj.vertices.length / 3;

  // Remap old vertex indices → their position in the appended tail block.
  const remap = new Map<number, number>();
  selected.forEach((vi, newIdx) => remap.set(vi, existingVertCount + newIdx));

  // Clone vertex positions directly (no centroid subtraction) so the duplicate
  // lands on top of the source.
  const newVerts = new Float32Array(obj.vertices.length + selected.length * 3);
  newVerts.set(obj.vertices);
  for (let i = 0; i < selected.length; i++) {
    const vi = selected[i];
    const writeBase = obj.vertices.length + i * 3;
    newVerts[writeBase] = obj.vertices[vi * 3];
    newVerts[writeBase + 1] = obj.vertices[vi * 3 + 1];
    newVerts[writeBase + 2] = obj.vertices[vi * 3 + 2];
  }

  // The remap already writes absolute tail-block indices into the extracted
  // sub-topology, so `concatTopology` is called with vertexOffset=0 to avoid
  // double-offsetting the corners.
  const selSet = new Set(selected);
  const subTopology = extractSubTopology(obj.topology, selSet, remap);
  const newTopology = concatTopology(obj.topology, subTopology, 0);
  const newIndices = triangulateTopologyForRendering(newTopology);
  const geo = {
    vertices: newVerts,
    indices: newIndices,
    topology: newTopology,
  };
  const nrPatch = computeNormalsForObject(obj, geo);

  const newSelection = new Set<number>();
  for (let i = 0; i < selected.length; i++) {
    newSelection.add(existingVertCount + i);
  }

  set({
    sceneObjects: replaceSceneObject(
      get().sceneObjects,
      em.objectId,
      patchWithUVs(obj, { ...geo, ...nrPatch }),
    ),
    editMode: { ...clearEditSelection(em), selectedVertices: newSelection },
  });
}

// ---------------------------------------------------------------------------
// Public re-exports — these are the cross-slice entry points. Other store
// slices / UI modules import from this module's index to avoid reaching
// into the sub-files.
// ---------------------------------------------------------------------------

export {
  applyResult,
  type EditClickHit,
  type EditClickModifiers,
  type EditOpResultCore,
  type EditStatePatch,
  patchSceneObjectAfterEdit,
  raycastEdit,
  registerEditRaycast,
  replaceSceneObjectAfterEdit,
  runEditOp,
} from "./core.ts";

export {
  applyVertexSelectionMode,
  type VertexSelectionMode,
} from "./selectionOps.ts";
